import {
  DashboardGlobalData,
  ProjectData,
  TaskData,
  UserData,
  InvoiceData,
  MeetingData,
  LeaveRequestData,
  InvoiceStats,
  TaskStats,
  ProjectStats,
} from '../../../components/advanced/dashboard/types';

// ============================================================================
// Mock Users
// ============================================================================

export const mockUsers: UserData[] = [
  { id: 'u1', name: 'John Smith', email: 'john@example.com', role: 'Project Manager', department: 'Engineering', title: 'Senior PM', status: 'active' },
  { id: 'u2', name: 'Sarah Johnson', email: 'sarah@example.com', role: 'Developer', department: 'Engineering', title: 'Lead Developer', status: 'active' },
  { id: 'u3', name: 'Michael Brown', email: 'michael@example.com', role: 'Designer', department: 'Design', title: 'UX Designer', status: 'away' },
  { id: 'u4', name: 'Emily Davis', email: 'emily@example.com', role: 'Developer', department: 'Engineering', title: 'Frontend Developer', status: 'active' },
  { id: 'u5', name: 'David Wilson', email: 'david@example.com', role: 'QA', department: 'Quality', title: 'QA Lead', status: 'busy' },
  { id: 'u6', name: 'Lisa Anderson', email: 'lisa@example.com', role: 'Developer', department: 'Engineering', title: 'Backend Developer', status: 'active' },
  { id: 'u7', name: 'Robert Taylor', email: 'robert@example.com', role: 'Manager', department: 'Operations', title: 'Operations Manager', status: 'active' },
  { id: 'u8', name: 'Jennifer Martinez', email: 'jennifer@example.com', role: 'HR', department: 'Human Resources', title: 'HR Specialist', status: 'offline' },
];

// ============================================================================
// Mock Projects
// ============================================================================

export const mockProjects: ProjectData[] = [
  {
    id: 'p1',
    name: 'Website Redesign',
    description: 'Complete overhaul of company website with new branding',
    status: 'in-progress',
    priority: 'high',
    progress: 65,
    ownerId: 'u1',
    memberIds: ['u2', 'u3', 'u4'],
    startDate: '2024-01-01',
    dueDate: '2024-03-15',
    budget: 50000,
    tags: ['design', 'frontend'],
    category: 'Development',
  },
  {
    id: 'p2',
    name: 'Mobile App v2.0',
    description: 'Major update to mobile application with new features',
    status: 'in-progress',
    priority: 'urgent',
    progress: 40,
    ownerId: 'u2',
    memberIds: ['u4', 'u6'],
    startDate: '2024-01-15',
    dueDate: '2024-04-30',
    budget: 75000,
    tags: ['mobile', 'react-native'],
    category: 'Development',
  },
  {
    id: 'p3',
    name: 'API Integration',
    description: 'Third-party API integrations for payment and analytics',
    status: 'planning',
    priority: 'medium',
    progress: 15,
    ownerId: 'u6',
    memberIds: ['u2', 'u5'],
    startDate: '2024-02-01',
    dueDate: '2024-05-15',
    budget: 30000,
    tags: ['backend', 'api'],
    category: 'Development',
  },
  {
    id: 'p4',
    name: 'Customer Portal',
    description: 'Self-service portal for customers',
    status: 'in-progress',
    priority: 'high',
    progress: 80,
    ownerId: 'u1',
    memberIds: ['u2', 'u3', 'u4', 'u5'],
    startDate: '2023-11-01',
    dueDate: '2024-02-28',
    budget: 60000,
    tags: ['frontend', 'ux'],
    category: 'Development',
  },
  {
    id: 'p5',
    name: 'Data Migration',
    description: 'Migrate legacy data to new database system',
    status: 'on-hold',
    priority: 'low',
    progress: 25,
    ownerId: 'u6',
    memberIds: ['u5'],
    startDate: '2024-01-20',
    dueDate: '2024-06-30',
    budget: 20000,
    tags: ['database', 'migration'],
    category: 'Infrastructure',
  },
  {
    id: 'p6',
    name: 'Security Audit',
    description: 'Complete security audit and vulnerability assessment',
    status: 'completed',
    priority: 'high',
    progress: 100,
    ownerId: 'u5',
    memberIds: ['u2', 'u6'],
    startDate: '2023-12-01',
    dueDate: '2024-01-31',
    endDate: '2024-01-28',
    budget: 25000,
    tags: ['security', 'audit'],
    category: 'Security',
  },
];

// ============================================================================
// Mock Tasks
// ============================================================================

export const mockTasks: TaskData[] = [
  { id: 't1', title: 'Design homepage mockup', description: 'Create initial mockups for homepage redesign', projectId: 'p1', assigneeId: 'u3', reporterId: 'u1', status: 'done', priority: 'high', tags: ['design'], dueDate: '2024-01-20', location: 'Office A' },
  { id: 't2', title: 'Implement authentication', description: 'Add OAuth2 authentication flow', projectId: 'p2', assigneeId: 'u2', reporterId: 'u1', status: 'in-progress', priority: 'high', tags: ['backend'], dueDate: '2024-02-15', location: 'Office A' },
  { id: 't3', title: 'Write unit tests', description: 'Add unit tests for user module', projectId: 'p1', assigneeId: 'u5', reporterId: 'u2', status: 'todo', priority: 'medium', tags: ['testing'], dueDate: '2024-02-20', location: 'Office B' },
  { id: 't4', title: 'API documentation', description: 'Document all API endpoints', projectId: 'p3', assigneeId: 'u6', reporterId: 'u1', status: 'in-progress', priority: 'low', tags: ['docs'], dueDate: '2024-03-01', location: 'Remote' },
  { id: 't5', title: 'Mobile UI review', description: 'Review and approve mobile UI designs', projectId: 'p2', assigneeId: 'u3', reporterId: 'u2', status: 'review', priority: 'high', tags: ['design', 'mobile'], dueDate: '2024-02-10', location: 'Office A' },
  { id: 't6', title: 'Database optimization', description: 'Optimize slow queries', projectId: 'p5', assigneeId: 'u6', reporterId: 'u5', status: 'blocked', priority: 'medium', tags: ['database'], dueDate: '2024-02-25', location: 'Office B' },
  { id: 't7', title: 'Deploy to staging', description: 'Deploy latest changes to staging environment', projectId: 'p4', assigneeId: 'u2', reporterId: 'u1', status: 'done', priority: 'high', tags: ['devops'], dueDate: '2024-01-25', location: 'Remote' },
  { id: 't8', title: 'User feedback analysis', description: 'Analyze user feedback from beta testing', projectId: 'p4', assigneeId: 'u3', reporterId: 'u1', status: 'in-progress', priority: 'medium', tags: ['ux'], dueDate: '2024-02-05', location: 'Office A' },
  { id: 't9', title: 'Fix login bug', description: 'Fix bug with social login', projectId: 'p2', assigneeId: 'u4', reporterId: 'u5', status: 'todo', priority: 'urgent', tags: ['bug'], dueDate: '2024-02-01', location: 'Office A' },
  { id: 't10', title: 'Performance testing', description: 'Run load tests on API', projectId: 'p3', assigneeId: 'u5', reporterId: 'u6', status: 'todo', priority: 'medium', tags: ['testing'], dueDate: '2024-03-15', location: 'Office B' },
];

// ============================================================================
// Mock Invoices
// ============================================================================

export const mockInvoices: InvoiceData[] = [
  { id: 'inv1', number: 'INV-2024-001', clientName: 'Acme Corp', amount: 15000, status: 'paid', dueDate: '2024-01-15', createdAt: '2024-01-01' },
  { id: 'inv2', number: 'INV-2024-002', clientName: 'TechStart Inc', amount: 8500, status: 'pending', dueDate: '2024-02-15', createdAt: '2024-01-20' },
  { id: 'inv3', number: 'INV-2024-003', clientName: 'Global Services', amount: 22000, status: 'paid', dueDate: '2024-01-30', createdAt: '2024-01-10' },
  { id: 'inv4', number: 'INV-2024-004', clientName: 'Local Business', amount: 3500, status: 'overdue', dueDate: '2024-01-20', createdAt: '2024-01-05' },
  { id: 'inv5', number: 'INV-2024-005', clientName: 'Startup Hub', amount: 12000, status: 'pending', dueDate: '2024-02-28', createdAt: '2024-02-01' },
  { id: 'inv6', number: 'INV-2024-006', clientName: 'Enterprise Co', amount: 45000, status: 'pending', dueDate: '2024-03-15', createdAt: '2024-02-10' },
  { id: 'inv7', number: 'INV-2024-007', clientName: 'Small Biz LLC', amount: 2800, status: 'draft', dueDate: '2024-03-01', createdAt: '2024-02-15' },
  { id: 'inv8', number: 'INV-2024-008', clientName: 'Tech Giants', amount: 65000, status: 'paid', dueDate: '2024-02-01', createdAt: '2024-01-15' },
];

// ============================================================================
// Mock Meetings
// ============================================================================

const now = new Date();
const tomorrow = new Date(now);
tomorrow.setDate(tomorrow.getDate() + 1);
const nextWeek = new Date(now);
nextWeek.setDate(nextWeek.getDate() + 7);

export const mockMeetings: MeetingData[] = [
  {
    id: 'm1',
    title: 'Project Kickoff',
    description: 'Kickoff meeting for new project',
    startTime: new Date(tomorrow.setHours(10, 0, 0, 0)).toISOString(),
    endTime: new Date(tomorrow.setHours(11, 0, 0, 0)).toISOString(),
    location: 'Conference Room A',
    attendeeIds: ['u1', 'u2', 'u3'],
    organizerId: 'u1',
    type: 'meeting',
  },
  {
    id: 'm2',
    title: 'Design Review',
    description: 'Review latest design mockups',
    startTime: new Date(tomorrow.setHours(14, 0, 0, 0)).toISOString(),
    endTime: new Date(tomorrow.setHours(15, 30, 0, 0)).toISOString(),
    location: 'Design Studio',
    attendeeIds: ['u3', 'u4'],
    organizerId: 'u3',
    type: 'presentation',
  },
  {
    id: 'm3',
    title: 'Sprint Planning',
    description: 'Plan next sprint tasks',
    startTime: new Date(nextWeek.setHours(9, 0, 0, 0)).toISOString(),
    endTime: new Date(nextWeek.setHours(10, 30, 0, 0)).toISOString(),
    location: 'Virtual - Zoom',
    attendeeIds: ['u1', 'u2', 'u4', 'u5', 'u6'],
    organizerId: 'u1',
    type: 'meeting',
  },
  {
    id: 'm4',
    title: 'Client Call',
    description: 'Weekly status update with client',
    startTime: new Date(tomorrow.setHours(16, 0, 0, 0)).toISOString(),
    endTime: new Date(tomorrow.setHours(16, 30, 0, 0)).toISOString(),
    location: 'Virtual - Teams',
    attendeeIds: ['u1', 'u7'],
    organizerId: 'u7',
    type: 'call',
  },
  {
    id: 'm5',
    title: 'Team Workshop',
    description: 'Agile methodology workshop',
    startTime: new Date(nextWeek.setHours(13, 0, 0, 0)).toISOString(),
    endTime: new Date(nextWeek.setHours(17, 0, 0, 0)).toISOString(),
    location: 'Training Room',
    attendeeIds: ['u1', 'u2', 'u3', 'u4', 'u5', 'u6'],
    organizerId: 'u1',
    type: 'workshop',
  },
];

// ============================================================================
// Mock Leave Requests
// ============================================================================

export const mockLeaveRequests: LeaveRequestData[] = [
  { id: 'l1', userId: 'u3', type: 'vacation', startDate: new Date().toISOString().split('T')[0], endDate: new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], status: 'approved', reason: 'Family vacation' },
  { id: 'l2', userId: 'u8', type: 'sick', startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], status: 'approved', reason: 'Not feeling well' },
  { id: 'l3', userId: 'u5', type: 'personal', startDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], endDate: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], status: 'pending', reason: 'Personal appointment' },
  { id: 'l4', userId: 'u2', type: 'vacation', startDate: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], endDate: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], status: 'approved', reason: 'Annual leave' },
];

// ============================================================================
// Computed Statistics
// ============================================================================

export function computeInvoiceStats(invoices: InvoiceData[]): InvoiceStats {
  return invoices.reduce(
    (acc, inv) => {
      acc.total++;
      acc.totalAmount += inv.amount;
      if (inv.status === 'pending') {
        acc.pending++;
        acc.pendingAmount += inv.amount;
      } else if (inv.status === 'paid') {
        acc.paid++;
        acc.paidAmount += inv.amount;
      } else if (inv.status === 'overdue') {
        acc.overdue++;
        acc.overdueAmount += inv.amount;
      }
      return acc;
    },
    { total: 0, totalAmount: 0, pending: 0, pendingAmount: 0, paid: 0, paidAmount: 0, overdue: 0, overdueAmount: 0 }
  );
}

export function computeTaskStats(tasks: TaskData[]): TaskStats {
  return tasks.reduce(
    (acc, task) => {
      acc.total++;
      if (task.status === 'todo') acc.todo++;
      else if (task.status === 'in-progress') acc.inProgress++;
      else if (task.status === 'review') acc.review++;
      else if (task.status === 'done') acc.done++;
      else if (task.status === 'blocked') acc.blocked++;

      // Check if overdue
      if (task.dueDate && task.status !== 'done') {
        const dueDate = new Date(task.dueDate);
        if (dueDate < new Date()) acc.overdue++;
      }
      return acc;
    },
    { total: 0, todo: 0, inProgress: 0, review: 0, done: 0, blocked: 0, overdue: 0 }
  );
}

export function computeProjectStats(projects: ProjectData[]): ProjectStats {
  const stats = projects.reduce(
    (acc, project) => {
      acc.total++;
      if (project.status === 'planning') acc.planning++;
      else if (project.status === 'in-progress') acc.inProgress++;
      else if (project.status === 'on-hold') acc.onHold++;
      else if (project.status === 'completed') acc.completed++;
      else if (project.status === 'cancelled') acc.cancelled++;
      acc.totalProgress += project.progress;
      return acc;
    },
    { total: 0, planning: 0, inProgress: 0, onHold: 0, completed: 0, cancelled: 0, totalProgress: 0 }
  );

  return {
    ...stats,
    averageProgress: stats.total > 0 ? Math.round(stats.totalProgress / stats.total) : 0,
  };
}

// ============================================================================
// Complete Dashboard Data
// ============================================================================

export function getMockDashboardData(): DashboardGlobalData {
  return {
    projects: mockProjects,
    tasks: mockTasks,
    users: mockUsers,
    invoices: mockInvoices,
    meetings: mockMeetings,
    leaveRequests: mockLeaveRequests,
    invoiceStats: computeInvoiceStats(mockInvoices),
    taskStats: computeTaskStats(mockTasks),
    projectStats: computeProjectStats(mockProjects),
  };
}
